import { AutogenLayoutMultiSelectRemoveCarrier } from "./autogen/AutogenLayoutMultiSelectRemoveCarrier";
import { Button, ClickEvent, DataSourceMode, Label, Layout, MainPageAccessor, Panel, ScreenStack, SlideoutDecorator, Snackbar, TableRow, TableRowDisplayEvent } from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { Api, Model, ModelRow } from "@mcleod/core";
import { ModifyCarrierPay } from "@mcleod/powerbroker";
import { OrderMultiSelectErrorModal } from "@mcleod/general/src/OrderMultiSelectErrorModal";
import { DataFusionUtil } from "@mcleod/datafusion";

export class MultiSelectRemoveCarrier extends AutogenLayoutMultiSelectRemoveCarrier {

    onLoad(): void | Promise<void> {
        this.tableMovements.addSelectionListener((_) =>
            this.labelTotalRequestCount.text = this.tableMovements.selectedRows.length.toString()
        );
    }

    tableMovementsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        const isManifest = !modelRow.isNull("manifest_id");
        const labelCustomerName = tableRow.findComponentById("labelCustomerName");
        const labelOrderId = tableRow.findComponentById("labelOrderId");
        const labelManifestId = tableRow.findComponentById("labelManifestId");
        const textboxManifestCustomers = tableRow.findComponentById("textboxManifestCustomers");
        labelOrderId.visible = !isManifest;
        labelCustomerName.visible = !isManifest;
        labelManifestId.visible = isManifest;
        textboxManifestCustomers.visible = isManifest;
    }

    static showSlideout(selectedRows: TableRow[], reasonCode?: string, reason?: string) {
        const layoutMultiSelectRemoveCarrier = Layout.getLayout('lme/dispatch/MultiSelectRemoveCarrier') as MultiSelectRemoveCarrier;

        const buttonRemoveCarriers = new Button({
            caption: 'Remove Carriers',
            rowBreak: false,
            backgroundColor: 'primary',
            borderWidth: 0
        });

        const decorator = new SlideoutDecorator({
            title: 'Multi-Select Remove Carriers',
            addlComponents: buttonRemoveCarriers,
            layout: layoutMultiSelectRemoveCarrier,
            fillVerticalSpace: true,
            width: '1200',
            displayOverlay: true,
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            doAfterSlideIn: () => {
                layoutMultiSelectRemoveCarrier.tableMovements.busy = true;
                layoutMultiSelectRemoveCarrier.mainDataSource.setRowsAndMode(DataSourceMode.NONE, selectedRows.map(row => row.data));
                layoutMultiSelectRemoveCarrier.tableMovements.selectedRows = layoutMultiSelectRemoveCarrier.tableMovements.allRows;
                layoutMultiSelectRemoveCarrier.labelTotalRequestCount.text = selectedRows.length.toString();
                layoutMultiSelectRemoveCarrier.tableMovements.busy = false;
            }
        });

        buttonRemoveCarriers.addClickListener((event: ClickEvent) => {
            const confirmedRows = layoutMultiSelectRemoveCarrier.tableMovements.selectedRows;
            if (confirmedRows.length === 0) {
                CommonDialogs.showError("Please select at least one row to remove carriers.", "Invalid Selection");
                return;
            }
            decorator.slideOut()
                    .then(() => this.handleConfirmedSelection(confirmedRows, reasonCode, reason))
                    .finally(() => selectedRows[0].table.selectedRows = []);
        })
    }

    static handleConfirmedSelection(confirmedRows: TableRow[], reasonCode: string, reason: string) {
        const isMulti = confirmedRows.length > 1;
        if (isMulti) {
            const downloadSnackbar = Snackbar.showDownloadSnackbar(
                "Multi-Select Action in Progress", `Removing carrier(s) for ${confirmedRows.length} movement(s)...`, {
                persist: true,
                targetPanel: ScreenStack.getSnackbarTarget()
            });
            return this.removeCarriers(confirmedRows, reasonCode, reason)
                .finally(() => {
                    confirmedRows[0].table.selectedRows = [];
                    downloadSnackbar.dismiss();
                });
        } else {//Single Selection
            const moveRow = confirmedRows[0];
            const moveId = moveRow.data.getString("movement_id");
            const carrierId = moveRow.data.getString("carrier_id", null);
            if (carrierId && !moveRow.data.getBoolean("is_brokerage_ltl")) {
                return CommonDialogs.showYesNo(`Would you like to pay the carrier for a TONU?`, "TONU Pay")
                    .then(async tonuYes => {
                        if (tonuYes) {
                            return Model.searchSingleRecord("lme/dispatch/payroll-control")
                            .then(pcData => this.openTONUSlideout(pcData.data?.tonu_earning_code, moveId, carrierId, () => this.singleUnassign(moveRow, reasonCode, reason)));
                        } else if (tonuYes === false) {
                            return this.singleUnassign(moveRow, reasonCode, reason);
                        }
                    });
            } else {
                return this.singleUnassign(moveRow, reasonCode, reason);
            }
        }
    }

    static async singleUnassign(moveRow: TableRow, reasonCode: string, reason: string): Promise<void> {
        const unassignSnackbar = Snackbar.showDownloadSnackbar("Unassign Carrier", "Unassigning...", {
            targetPanel: MainPageAccessor.router
        });

        try {
            await this.removeCarriers([moveRow], reasonCode, reason);
        } finally {
            unassignSnackbar.dismiss();
        }
    }

    /**
     * Remove carriers from the selected movements.
     * For movements where the carrier was successfully removed:
     *     - Brokerage Tracking will be cancelled if br_vendor found
     *     - Data Fusion Cancel Tender will be sent if Cancel allowed for carrier/movement pair
     */
    static async removeCarriers(confirmedRows: TableRow[], reasonCode?: string, reason?: string) {
        //Remove Carrier(s)
        const cancelDispatchStatusList = await this.cancelDispatch(confirmedRows, reasonCode, reason);

        //Parse for response success and failure
        const carrierRemovedIds: string[] = [];
        const removalFailed: {movement_id: string, success: boolean, error: string}[] = [];
        cancelDispatchStatusList.forEach(status => {
            if (status.success) {
                carrierRemovedIds.push(status.movement_id);
            } else {
                removalFailed.push(status);
            }
        });
        const carrierRemovedRows = confirmedRows.filter(row => carrierRemovedIds.includes(row.data.getString('movement_id')));

        //Cancel Brokerage Tracking for movements where carrier was removed and tracking initiated
        const rowsToRemoveTracking = carrierRemovedRows.filter(row => !row.data.isNull('br_vendor'));
        const cancelTrackingStatusList = (rowsToRemoveTracking.length) ? await this.cancelTracking(rowsToRemoveTracking) : [];
        const cancelFailed: {movement_id: string, success: boolean, error: string, vendor_name: string, force_cancel: boolean}[] = [];
        cancelTrackingStatusList.forEach(status => {
            if (!status.success) {
                cancelFailed.push(status);
            }
        });

        //Send Data Fusion Cancel Tender for movements where carrier was removed and cancel is allowed
        const cancelAllowedStatus = await this.isCancelAllowed(carrierRemovedRows);
        const cancelAllowedIds: string[] = cancelAllowedStatus.filter(status => !!status?.allowed).map(status => status.movementId);
        const rowsToSendCancelTender = carrierRemovedRows.filter(row => cancelAllowedIds.includes(row.data.getString('movement_id')));
        const dfCancelQuery = await this.makeDFCancelQuery(rowsToSendCancelTender);
        //Process cancel tender requests synchronously to keep batch ids consistent
        const dfCancelResponses = [];
        for (const row of dfCancelQuery) {
            const response = await DataFusionUtil.sendCancelTender(row.carrierId, row.movementId, false);
            dfCancelResponses.push(response);
        }
        const dfCancelSuccessCount = dfCancelResponses.filter(response => response.data.tendersent).length;
        const dfCancelFailed = dfCancelResponses.filter(response => !response.data.tendersent).map(response => response.movementId);
        const panelCancelTendersSent = this.buildCancelTenderPanel(dfCancelSuccessCount);

        const updatedCount = carrierRemovedRows.length;
        const failedRows = confirmedRows.filter(row => removalFailed.some(error => error.movement_id === row.data.getString("movement_id")));
        failedRows.forEach(row => row.data.set("error", removalFailed.find(error => error.movement_id === row.data.getString("movement_id"))?.error));

        const errorRows = [];
        const brTrackingCancelErrorRows = rowsToRemoveTracking
                                        .filter(row => cancelFailed.some(error => error.movement_id === row.data.getString("movement_id")))
                                        .map(row => row.data.createBasicCopy());
        brTrackingCancelErrorRows.forEach(row => row.set("error", cancelFailed.find(error => error.movement_id === row.getString("movement_id"))?.error));

        errorRows.push(...brTrackingCancelErrorRows);

        const dfCancelErrorRows = rowsToSendCancelTender
                                .filter(row => dfCancelFailed.includes(row.data.getString("movement_id")))
                                .map(row => row.data.createBasicCopy());
        dfCancelErrorRows.forEach(row => row.set("error", "Failed to send Cancel Tender"));
        errorRows.push(...dfCancelErrorRows);

        OrderMultiSelectErrorModal.showModal(failedRows, errorRows, updatedCount, "Multi-Select Carrier Removal", "Retry", (selectedErrorRows) => this.removeCarriers(selectedErrorRows, reasonCode, reason), [panelCancelTendersSent]);
    }

    static buildCancelTenderPanel(dfCancelSuccessCount: number) {
        const panel = new Panel({ fillRow: true, id: "panelCancelTendersSent" });
        const labelCancelTendersSent = new Label({
            fontBold: true,
            id: "labelCancelTendersSent",
            paddingRight: 0,
            rowBreak: false,
            text: dfCancelSuccessCount.toString()
        });
        const labelCancelTenders = new Label({
            id: "labelCancelTenders",
            paddingLeft: 0,
            rowBreak: false,
            text: " Cancel Tender(s) Sent"
        });
        panel.add(labelCancelTendersSent, labelCancelTenders);
        return panel;
    }

    static async cancelDispatch(confirmedRows: TableRow[], reasonCode?: string, reason?: string) {
        const response = await Api.post("lme/dispatch/cancel-dispatch", {
            movement_ids: confirmedRows.map(row => row.data.getString('movement_id')),
            reason_code: reasonCode,
            comment: reason
        });

        return response?.data;
    }

    static async cancelTracking(rowsToRemoveTracking: TableRow[]) {
        const response = await Api.post("lme/powerbroker/tracking/cancel-tracking", {
            "movement_ids": rowsToRemoveTracking.map(row => row.data.getString('movement_id'))
        });
        return response?.data ?? [];
    }

    static makeDFCancelQuery(carrierRemovedRows: TableRow[]) {
        return carrierRemovedRows.map(row => ({
            carrierId: row.data.getString('carrier_id', null),
            movementId: row.data.getString('movement_id')
        }));
    }

    static async isCancelAllowed(carrierRemovedRows: TableRow[]) {
        const cancelAllowedQuery = this.makeDFCancelQuery(carrierRemovedRows);

        return await DataFusionUtil.areCancelsAllowed(cancelAllowedQuery);
    }

    static openTONUSlideout(tonu_earning_code: String, moveId: string, carrierId: string, doAfterSlideOut: () => Promise<void>) {
        ModifyCarrierPay.showSlideout(moveId, carrierId, async (movementSaved: boolean, moveRow: ModelRow, extraPayRows: ModelRow[]) => {
            if (doAfterSlideOut) await doAfterSlideOut();
            this.addTONUCharges(extraPayRows, moveRow.get("id"));
        }, tonu_earning_code);
    }

    static addTONUCharges(tonuCharges: Array<ModelRow>, movementID: String) {
        tonuCharges.forEach(async (row: ModelRow) => {
            const modelRow = new ModelRow("lme/powerbroker/extra-pay", row._appending);
            modelRow.setValues(row.data);
            modelRow.set("movement_id", movementID);
            await Api.update("lme/powerbroker/extra-pay", modelRow);
        })
    }
}
